import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import "./Seekers.css";

interface Seeker {
  name: string;
  title: string;
  image: string;
  avatarImage: string;
  description: string;
}

const seekers: Seeker[] = [
  {
    name: "Cecil",
    title: "Warden of the Dawn",
    image: "seekers/cecil.webp",
    avatarImage: "seekers/avatar/cecil.webp",
    description:
      "A holy knight wielding divine power to protect the innocent and vanquish evil.",
  },
  {
    name: "Ithril",
    title: "Lich of the Scarlet Rite",
    image: "seekers/ithril.webp",
    avatarImage: "seekers/avatar/ithril.webp",
    description:
      "An undead sorcerer commanding dark magic and necromantic powers.",
  },
  {
    name: "Lyra",
    title: "Hand of the Grave",
    image: "seekers/lyra.webp",
    avatarImage: "seekers/avatar/lyra.webp",
    description:
      "A deadly assassin who strikes from the shadows with precision and stealth.",
  },
  {
    name: "Theron",
    title: "the Ashwaker",
    image: "seekers/theron.webp",
    avatarImage: "seekers/avatar/theron.webp",
    description:
      "A fire mage who channels the elemental fury of flames and destruction.",
  },
  {
    name: "Mara",
    title: "Veiled Blade",
    image: "seekers/Mara.webp",
    avatarImage: "seekers/avatar/mara.webp",
    description:
      "A shadowy warrior skilled in both blade work and mystical arts.",
  },
  {
    name: "Ranokage",
    title: "Storm Shinobi",
    image: "seekers/ranokage.webp",
    avatarImage: "seekers/avatar/ranokage.webp",
    description:
      "A lightning-fast ninja harnessing the power of storms and electricity.",
  },
  {
    name: "Ysara",
    title: "Queen of Fang and Fire",
    image: "seekers/ysara.webp",
    avatarImage: "seekers/avatar/ysara.webp",
    description: "A draconic sorceress commanding both flame and bestial fury.",
  },
];

const Seekers: React.FC = () => {
  const [activeSeeker, setActiveSeeker] = useState(0);
  const [selectedSeeker, setSelectedSeeker] = useState<number | null>(null);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  return (
    <div id="seekers" className="seekers-subsection" ref={ref}>
      <div className="container">
        <motion.div
          className="seekers-content"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {/* Header */}
          <motion.div className="seekers-header" variants={itemVariants}>
            <h3>
              With dozens of Seekers to choose from,
              <br />
              who will <em>you</em> be in the world of Imriel?
            </h3>
            <p>
              At the heart of every deck's strategy is the Seeker you choose to play as. Each Seeker comes with a powerful, strategy-defining passive ability and a stat line of five attributes — Strength, Dexterity, Intelligence, Spirit, and Constitution — which determine the equipment, abilities, and items they can use. There are no fixed classes, giving you complete freedom to experiment, theorycraft, and customize your playstyle any way you want. If you want to play a rogue who fights from stealth with ice magic, or a warrior who draws power from demonic forces, you can do so by choosing a Seeker with the right stat line and building your Armory around it.
            </p>
          </motion.div>

          {/* Hero Showcase */}
          <motion.div className="seekers-showcase" variants={itemVariants}>
            {/* Character Sidebar */}
            <div className="character-sidebar">
              {seekers.map((seeker, index) => (
                <motion.button
                  key={seeker.name}
                  className={`character-face ${
                    index === activeSeeker ? "active" : ""
                  }`}
                  onClick={() => setActiveSeeker(index)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  title={`${seeker.name}, ${seeker.title}`}
                >
                  <img src={seeker.avatarImage} alt={seeker.name} />
                  <div className="face-overlay"></div>
                </motion.button>
              ))}
            </div>

            {/* Featured Card Display */}
            <div className="featured-display">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeSeeker}
                  className="featured-card"
                  initial={{ opacity: 0, x: 50, scale: 0.95 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={{ opacity: 0, x: -50, scale: 0.95 }}
                  transition={{
                    duration: 0.5,
                    ease: "easeInOut",
                  }}
                  onClick={() => setSelectedSeeker(activeSeeker)}
                  whileHover={{ scale: 1.02 }}
                  style={{ cursor: 'pointer' }}
                >

                  
                  <div className="featured-image">
                    <img
                      src={seekers[activeSeeker].image}
                      alt={`${seekers[activeSeeker].name}, ${seekers[activeSeeker].title}`}
                      loading="lazy"
                    />
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Card Modal */}
      <AnimatePresence>
        {selectedSeeker !== null && (
          <motion.div
            className="seeker-modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedSeeker(null)}
          >
            <motion.div
              className="seeker-modal"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button 
                className="seeker-modal-close"
                onClick={() => setSelectedSeeker(null)}
              >
                ×
              </button>
              <img 
                src={seekers[selectedSeeker].image} 
                alt={`${seekers[selectedSeeker].name}, ${seekers[selectedSeeker].title}`}
              />
              <div className="modal-info">
                <h3>{seekers[selectedSeeker].name}</h3>
                <h4>{seekers[selectedSeeker].title}</h4>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Seekers;
